// SalesAnalyticsView.js — iter208
//
// REPORT-TYPE-DRIVEN FILTER LOGIC: the active report type determines which
// time controls are visible. We never show FY + Quarter + Month + Range
// simultaneously.
//
//   • Daily      → Portfolio · Outlet · From · To
//   • Monthly    → Portfolio · Outlet · FY
//   • Quarterly  → Portfolio · Outlet · FY
//   • FY         → Portfolio · Outlet · multi-FY
//
// Reporting layer only — all numbers come from /api/sales-analytics/* which
// itself reuses calculate_metrics + compute_cost_with_cutover from the live
// Dashboard. No new finance logic.
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid,
} from 'recharts';
import { ArrowUpRight, ArrowDownRight, Minus, Download, FileText } from 'lucide-react';

import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { OutletFilter } from '../components/OutletFilter';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// ── Helpers ───────────────────────────────────────────────────────
// iter209 — business-friendly formatters:
//   • fmt0   → whole rupees with Indian comma grouping ("12,45,678")
//   • fmt1   → max 1 decimal ("486.5")
//   • fmtPct → 1 decimal + %  ("23.4%")
//   • fmtMonth ('2026-04') → 'Apr-26' for headers and exports
const fmt0   = (n) => (n == null || isNaN(n)) ? '—' : Math.round(Number(n)).toLocaleString('en-IN');
const fmt1   = (n) => (n == null || isNaN(n)) ? '—' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
const fmtPct = (n) => (n == null) ? '—' : `${Number(n).toFixed(1)}%`;
const _MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtMonth = (key) => {
  // Accepts 'YYYY-MM' or any non-month-like string (returned unchanged).
  if (!key || typeof key !== 'string') return key;
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (!m) return key;
  const yr = m[1].slice(-2);
  const mn = _MONTH_NAMES[parseInt(m[2], 10) - 1] || m[2];
  return `${mn}-${yr}`;
};

// Per-metric formatter routing.
const _SALES_METRICS = new Set([
  'net_sales', 'total_revenue', 'food_sale', 'beverage_sale',
  'food_cost', 'beverage_cost', 'room_revenue',
]);
const _APC_METRICS = new Set([
  'food_apc', 'beverage_apc', 'total_apc', 'arr', 'revpar',
]);
const _COUNT_METRICS = new Set(['covers', 'orders', 'rooms_sold']);
const _PCT_METRICS  = new Set(['food_cost_pct', 'beverage_cost_pct', 'occupancy_pct']);

function fmtMetric(metric, value) {
  if (value == null) return '—';
  if (_PCT_METRICS.has(metric))   return fmtPct(value);
  if (_SALES_METRICS.has(metric)) return fmt0(value);
  if (_APC_METRICS.has(metric))   return fmt1(value);
  if (_COUNT_METRICS.has(metric)) return fmt0(value);
  // Fallback for unknown metric → integer.
  return fmt0(value);
}

// Fallback labels — the backend also ships `metric_labels` alongside the
// applicable_metrics/groups payload. Prefer the backend version at
// render time; this map exists so an unknown metric still renders.
const METRIC_LABEL = {
  total_revenue: 'Total Sales',
  net_sales: 'F&B Net Sales',
  food_sale: 'Food Sales', beverage_sale: 'Beverage Sales',
  covers: 'Covers', orders: 'Orders',
  food_apc: 'Food APC', beverage_apc: 'Beverage APC', total_apc: 'Total APC',
  food_cost: 'Food Cost', beverage_cost: 'Beverage Cost',
  food_cost_pct: 'Food Cost %', beverage_cost_pct: 'Beverage Cost %',
  room_revenue: 'Room Revenue', rooms_sold: 'Rooms Sold',
  occupancy_pct: 'Occupancy %', arr: 'ARR', revpar: 'RevPAR',
};

function Indicator({ ind, metric }) {
  if (!ind || ind.arrow === 'na') return null;
  const cls = ind.arrow === 'up' ? 'text-emerald-600'
            : ind.arrow === 'down' ? 'text-rose-600'
            : 'text-slate-500';
  const Icon = ind.arrow === 'up' ? ArrowUpRight
             : ind.arrow === 'down' ? ArrowDownRight : Minus;
  const cur = metric ? fmtMetric(metric, ind.current)    : fmt0(ind.current);
  const cmp = metric ? fmtMetric(metric, ind.comparison) : fmt0(ind.comparison);
  const sign = ind.growth_pct >= 0 ? '+' : '';
  const title = `Current ${cur} · Compare ${cmp} · ${sign}${Number(ind.growth_pct).toFixed(1)}%`;
  return <Icon className={`inline-block w-3.5 h-3.5 ml-1 ${cls}`} title={title} data-testid="sa-indicator"/>;
}

function ValueCell({ value, ind, metric }) {
  return (
    <span className="whitespace-nowrap tabular-nums">
      {fmtMetric(metric, value)}<Indicator ind={ind} metric={metric}/>
    </span>
  );
}

function MatrixTable({ labels, matrix, metrics, summary, summaryLabel = 'Total', metricLabels = {} }) {
  if (!labels?.length) {
    return <div className="text-sm text-slate-500 p-6 text-center">No data in this window.</div>;
  }
  const labelFor = (m) => metricLabels[m] || METRIC_LABEL[m] || m;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="sa-matrix">
      <table className="min-w-full text-xs">
        <thead className="bg-[#0E5236] text-white sticky top-0">
          <tr>
            <th className="text-left px-3 py-2 sticky left-0 bg-[#0E5236] z-10 min-w-[160px]">KPI</th>
            {labels.map((d) => (
              <th key={d} className="text-right px-3 py-2 min-w-[110px] whitespace-nowrap">{fmtMonth(d)}</th>
            ))}
            <th className="text-right px-3 py-2 bg-[#0B3E29] min-w-[110px]">{summaryLabel}</th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {metrics.map((m, i) => (
              <tr key={m} className={i % 2 ? 'bg-slate-50' : 'bg-white'} data-testid={`sa-matrix-row-${m}`}>
                <td className="px-3 py-1.5 sticky left-0 bg-inherit font-medium text-slate-700">
                  {labelFor(m)}
                </td>
                {labels.map((d) => {
                  const row = matrix[d] || {};
                  return (
                    <td key={d} className="px-3 py-1.5 text-right">
                      <ValueCell value={row[m]} ind={row[`${m}_ind`]} metric={m}/>
                    </td>
                  );
                })}
                <td className="px-3 py-1.5 text-right font-semibold text-[#0E5236] bg-emerald-50/40">
                  <ValueCell value={summary?.[m]} metric={m}/>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function KpiCards({ totals, groups, labels }) {
  if (!totals || !Array.isArray(groups) || groups.length === 0) return null;
  const labelFor = (m) => (labels && labels[m]) || METRIC_LABEL[m] || m;
  return (
    <div className="space-y-3" data-testid="sa-kpi-cards">
      {groups.map((grp) => (
        <div key={grp.id} data-testid={`sa-kpi-group-${grp.id}`}>
          <div className="text-xs uppercase tracking-wider text-slate-500 pt-1">
            {grp.label}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-1">
            {grp.metrics.map((m) => (
              <Card key={m} data-testid={`sa-kpi-${m}`}
                     className="p-3 border border-slate-200">
                <div className="text-[11px] uppercase text-slate-500 tracking-wide">
                  {labelFor(m)}
                </div>
                <div className="text-lg font-semibold text-[#0E5236] tabular-nums">
                  {fmtMetric(m, totals[m])}
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ChannelMixCard({ data }) {
  if (!data?.items?.length) return null;
  return (
    <Card className="p-4 border border-slate-200 mt-4" data-testid="sa-channel-mix">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-sm font-semibold text-slate-700">Channel Mix</div>
        <div className="text-xs text-slate-500">{data.window?.label}</div>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-500 uppercase text-[10px] tracking-wider">
            <th className="text-left py-1">Channel</th>
            <th className="text-right py-1">Sales</th>
            <th className="text-right py-1">Share</th>
            <th className="text-right py-1">Orders</th>
            <th className="text-right py-1">APC</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((it) => (
            <tr key={it.channel} className="border-t border-slate-100">
              <td className="py-1.5 capitalize text-slate-700">{it.channel.replace('_', ' ')}</td>
              <td className="py-1.5 text-right tabular-nums">{fmt0(it.sales)}</td>
              <td className="py-1.5 text-right tabular-nums">{fmtPct(it.contribution_pct)}</td>
              <td className="py-1.5 text-right tabular-nums">{fmt0(it.orders)}</td>
              <td className="py-1.5 text-right tabular-nums">{fmt1(it.apc)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}


// ── Channel Mix · Month-on-Month (Feb 2026) ─────────────────────────
// Rendered directly below the existing Channel Mix table. KPI selector
// (Sales | Share | Orders | APC) reuses the same values the current card
// shows — no new formulas.
const FY_MONTHS = ['04','05','06','07','08','09','10','11','12','01','02','03'];
const MONTH_LABEL = { '01':'Jan','02':'Feb','03':'Mar','04':'Apr','05':'May','06':'Jun',
                       '07':'Jul','08':'Aug','09':'Sep','10':'Oct','11':'Nov','12':'Dec' };

function ChannelMixMonthlyCard({ data, fyStartYear }) {
  const [kpi, setKpi] = useState('sales'); // 'sales' | 'share' | 'orders' | 'apc'
  if (!data?.monthly?.channels?.length) return null;

  const { channels, cells } = data.monthly;
  // FY months in the correct Apr→Mar order
  const monthKeys = FY_MONTHS.map(m => {
    // Apr-Dec live in fyStartYear; Jan-Mar in fyStartYear+1
    const yr = parseInt(m, 10) >= 4 ? fyStartYear : fyStartYear + 1;
    return `${yr}-${m}`;
  });

  const fmtVal = (v) => {
    if (v == null) return '—';
    if (kpi === 'sales')  return fmt0(v);
    if (kpi === 'share')  return fmtPct(v);
    if (kpi === 'orders') return fmt0(v);
    return fmt1(v);
  };

  const readCell = (ym, ch) => {
    const cell = cells[ym]?.[ch];
    if (!cell) return null;
    if (kpi === 'sales')  return cell.sales;
    if (kpi === 'share')  return cell.contribution_pct;
    if (kpi === 'orders') return cell.orders;
    return cell.apc;
  };

  const movementFor = (curVal, prevVal) => {
    if (curVal == null || prevVal == null) return null;
    if (kpi === 'share') {
      // Percentage-point movement (not growth %) — spec §3
      const delta = curVal - prevVal;
      return { delta, isPp: true };
    }
    if (prevVal === 0) return { new: true };
    const pct = (curVal - prevVal) / prevVal * 100;
    return { pct };
  };

  const movementLabel = (m) => {
    if (!m) return '';
    if (m.new) return 'New';
    if (m.isPp) {
      const s = m.delta >= 0 ? '▲' : '▼';
      return `${s} ${Math.abs(m.delta).toFixed(1)} pp`;
    }
    const s = m.pct >= 0 ? '▲' : '▼';
    return `${s} ${Math.abs(m.pct).toFixed(1)}%`;
  };

  const movementColor = (m) => {
    if (!m || m.new) return 'text-slate-400';
    const pos = (m.isPp ? m.delta : m.pct) >= 0;
    return pos ? 'text-emerald-600' : 'text-rose-600';
  };

  return (
    <Card className="p-4 border border-slate-200 mt-4" data-testid="sa-channel-mix-monthly">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div className="text-sm font-semibold text-slate-700">Month-on-Month Channel Performance</div>
        <div className="flex gap-1" data-testid="channel-mom-kpi-selector">
          {[
            { k: 'sales',  label: 'Sales' },
            { k: 'share',  label: 'Share' },
            { k: 'orders', label: 'Orders' },
            { k: 'apc',    label: 'APC' },
          ].map(({ k, label }) => (
            <button key={k}
              onClick={() => setKpi(k)}
              className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                kpi === k
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
              data-testid={`channel-mom-kpi-${k}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="channel-mom-matrix">
          <thead>
            <tr className="text-slate-500 uppercase text-[10px] tracking-wider">
              <th className="text-left py-1 sticky left-0 bg-white z-10">Channel</th>
              {monthKeys.map(ym => (
                <th key={ym} className="text-right py-1 px-2 min-w-[72px]">
                  {MONTH_LABEL[ym.slice(5, 7)]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {channels.map(ch => (
              <tr key={ch} className="border-t border-slate-100" data-testid={`channel-mom-row-${ch}`}>
                <td className="py-1.5 capitalize text-slate-700 sticky left-0 bg-white z-10">
                  {ch.replace('_', ' ')}
                </td>
                {monthKeys.map((ym, idx) => {
                  const val = readCell(ym, ch);
                  const prevYm = idx > 0 ? monthKeys[idx - 1] : null;
                  const prevVal = prevYm ? readCell(prevYm, ch) : null;
                  const mv = movementFor(val, prevVal);
                  return (
                    <td key={ym} className="py-1.5 px-2 text-right tabular-nums align-top">
                      <div className="text-slate-700" data-testid={`channel-mom-${ch}-${ym}`}>
                        {fmtVal(val)}
                      </div>
                      {idx > 0 && (
                        <div className={`text-[10px] mt-0.5 ${movementColor(mv)}`}>
                          {mv ? movementLabel(mv) : '—'}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Totals row — Total APC = Σ sales / Σ orders (never avg of channel APCs) */}
            <tr className="border-t-2 border-slate-300 bg-slate-50">
              <td className="py-1.5 font-semibold text-slate-700 sticky left-0 bg-slate-50 z-10">TOTAL</td>
              {monthKeys.map((ym, idx) => {
                const tot = cells[ym]?._total;
                if (!tot) return <td key={ym} className="py-1.5 px-2 text-right tabular-nums">—</td>;
                let val;
                if (kpi === 'sales')  val = tot.sales;
                else if (kpi === 'share') val = tot.sales > 0 ? 100 : null;
                else if (kpi === 'orders') val = tot.orders;
                else val = tot.apc;
                const prevYm = idx > 0 ? monthKeys[idx - 1] : null;
                const prevTot = prevYm ? cells[prevYm]?._total : null;
                let prev = null;
                if (prevTot) {
                  if (kpi === 'sales')  prev = prevTot.sales;
                  else if (kpi === 'share') prev = prevTot.sales > 0 ? 100 : null;
                  else if (kpi === 'orders') prev = prevTot.orders;
                  else prev = prevTot.apc;
                }
                const mv = movementFor(val, prev);
                return (
                  <td key={ym} className="py-1.5 px-2 text-right tabular-nums align-top font-semibold">
                    <div className="text-slate-800">{fmtVal(val)}</div>
                    {idx > 0 && (
                      <div className={`text-[10px] mt-0.5 font-normal ${movementColor(mv)}`}>
                        {mv ? movementLabel(mv) : '—'}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Main component ──────────────────────────────────────────────
export default function SalesAnalyticsView({ viewerMode = 'admin' } = {}) {
  const { getHeaders, API, user } = useAuth();
  const isManagerView = viewerMode === 'manager';
  const managerAssigned = useMemo(
    () => (user?.assigned_outlets || []).map((o) => o.id).filter(Boolean),
    [user],
  );
  const today = new Date();
  const currentFY = today.getMonth() + 1 >= 4 ? today.getFullYear() : today.getFullYear() - 1;

  // Report type: 'daily' | 'monthly' | 'quarterly' | 'fy' | 'trend' | 'channel'
  const [reportType, setReportType] = useState(() => {
    try { return sessionStorage.getItem('sa_report') || 'daily'; } catch { return 'daily'; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('sa_report', reportType); } catch { /* noop */ }
  }, [reportType]);

  // Shared filters (always shown).
  // ITER240 FIX — outlet/portfolio selection is staged in "pending" state
  // and only committed to the live state by an explicit Apply click.
  // This prevents a fetch storm + stale-response race when the user ticks
  // multiple outlets in quick succession.
  const [portfolioId, setPortfolioId] = useState('');
  const [outletIds,   setOutletIds]   = useState(() => (isManagerView ? managerAssigned : []));
  const [pendingPortfolioId, setPendingPortfolioId] = useState('');
  const [pendingOutletIds,   setPendingOutletIds]   = useState(() => (isManagerView ? managerAssigned : []));
  // Manager view — keep outletIds locked to the authenticated manager's
  // assignment. Fires whenever auth hydrates.
  useEffect(() => {
    if (!isManagerView) return;
    if (managerAssigned.length === 0) return;
    setPortfolioId('');
    setOutletIds(managerAssigned);
    setPendingPortfolioId('');
    setPendingOutletIds(managerAssigned);
  }, [isManagerView, managerAssigned]);
  const scopeDirty = (
    pendingPortfolioId !== portfolioId ||
    pendingOutletIds.length !== outletIds.length ||
    pendingOutletIds.some(id => !outletIds.includes(id))
  );
  const applyScope = useCallback(() => {
    setPortfolioId(pendingPortfolioId);
    setOutletIds(pendingOutletIds);
  }, [pendingPortfolioId, pendingOutletIds]);
  const resetScope = useCallback(() => {
    setPendingPortfolioId(portfolioId);
    setPendingOutletIds(outletIds);
  }, [portfolioId, outletIds]);

  // Catalogs — resolved once for export-header name lookup.
  const [portfolioMap, setPortfolioMap] = useState({});
  const [outletMap,    setOutletMap]    = useState({});
  useEffect(() => {
    (async () => {
      try {
        const headers = getHeaders();
        const [pRes, oRes] = await Promise.all([
          axios.get(`${API}/admin/portfolios`, { headers }).catch(() => null),
          axios.get(`${API}/outlets`, { headers }).catch(() => null),
        ]);
        if (pRes?.data) {
          const list = pRes.data?.data || pRes.data?.portfolios || pRes.data || [];
          const m = {};
          (Array.isArray(list) ? list : []).forEach(p => {
            if (p?.id) m[p.id] = p.name || p.label || p.id;
          });
          setPortfolioMap(m);
        }
        if (oRes?.data) {
          const list = oRes.data?.data || oRes.data?.outlets || oRes.data || [];
          const m = {};
          (Array.isArray(list) ? list : []).forEach(o => {
            if (o?.id) m[o.id] = o.name || o.label || o.id;
          });
          setOutletMap(m);
        }
      } catch (e) { /* names will fall back to IDs */ }
    })();
  }, [API, getHeaders]);
  const portfolioName = portfolioId ? (portfolioMap[portfolioId] || portfolioId) : 'All Portfolios';
  const outletNames = outletIds.length
    ? outletIds.map(id => outletMap[id] || id).join(', ')
    : 'All Outlets';

  // Report-specific filters
  const [fyStartYear, setFyStartYear] = useState(currentFY);
  const [fySelectionCsv, setFySelectionCsv] = useState(
    [currentFY - 2, currentFY - 1, currentFY].join(','));
  const yesterday = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; }, []);
  const sevenAgo  = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d; }, []);
  const [rangeStart, setRangeStart] = useState(sevenAgo);
  const [rangeEnd,   setRangeEnd]   = useState(yesterday);
  const [rangeStartOpen, setRangeStartOpen] = useState(false);
  const [rangeEndOpen,   setRangeEndOpen]   = useState(false);

  // Trend-only (within report type 'trend'). Default to total_revenue —
  // the canonical Sales metric that includes rooms when applicable.
  const [trendMetric, setTrendMetric] = useState('total_revenue');
  const [trendGran,   setTrendGran]   = useState('monthly');

  // Computed scope query string
  const scopeQS = useMemo(() => {
    const parts = [];
    if (portfolioId) parts.push(`portfolio_ids=${portfolioId}`);
    if (outletIds.length) parts.push(`outlet_ids=${outletIds.join(',')}`);
    return parts.length ? `&${parts.join('&')}` : '';
  }, [portfolioId, outletIds]);

  // Buckets per report
  const [busy, setBusy] = useState(false);
  const [daily, setDaily]         = useState(null);
  const [monthly, setMonthly]     = useState(null);
  const [quarterly, setQuarterly] = useState(null);
  const [fyComp, setFyComp]       = useState(null);
  const [trend, setTrend]         = useState(null);
  const [mix, setMix]             = useState(null);
  const [mixMonthly, setMixMonthly] = useState(null);
  const [kpis, setKpis]           = useState(null);

  // KPI summary fetcher — uses the report's own window
  const kpiQS = useMemo(() => {
    if (reportType === 'daily') {
      return `start_date=${format(rangeStart, 'yyyy-MM-dd')}&end_date=${format(rangeEnd, 'yyyy-MM-dd')}`;
    }
    return `fy_start_year=${fyStartYear}`;
  }, [reportType, rangeStart, rangeEnd, fyStartYear]);

  // ITER240 FIX — eliminate the stale-response race.
  //
  // Each fetchReport() invocation gets a monotonically increasing seq id
  // AND an AbortController. Before applying any response to state we
  // (a) check the seq is still the latest, and (b) check the abort
  // signal wasn't fired. The previous in-flight request is actively
  // aborted when a newer one starts, so axios drops it; even if it
  // races us we will never overwrite the UI with a stale answer.
  const reqSeqRef = useRef(0);
  const abortRef  = useRef(null);
  // First OutletFilter onChange (localStorage hydration) — sync live + pending.
  const hydratedRef = useRef(false);

  const fetchReport = useCallback(async () => {
    // Cancel any previous in-flight request.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const mySeq = ++reqSeqRef.current;
    const isLatest = () => mySeq === reqSeqRef.current && !controller.signal.aborted;

    setBusy(true);
    try {
      const headers = getHeaders();
      const reqOpts = { headers, signal: controller.signal };
      // KPI cards always for the active window
      const kRes = await axios.get(`${API}/sales-analytics/kpis?${kpiQS}${scopeQS}`, reqOpts);
      if (!isLatest()) return;
      setKpis(kRes.data.data);
      if (reportType === 'daily') {
        const s = format(rangeStart, 'yyyy-MM-dd');
        const e = format(rangeEnd, 'yyyy-MM-dd');
        const r = await axios.get(`${API}/sales-analytics/daily?start_date=${s}&end_date=${e}${scopeQS}`, reqOpts);
        if (!isLatest()) return;
        setDaily(r.data.data);
      } else if (reportType === 'monthly') {
        const r = await axios.get(`${API}/sales-analytics/monthly?fy_start_year=${fyStartYear}${scopeQS}`, reqOpts);
        if (!isLatest()) return;
        setMonthly(r.data.data);
      } else if (reportType === 'quarterly') {
        const r = await axios.get(`${API}/sales-analytics/quarterly?fy_start_year=${fyStartYear}${scopeQS}`, reqOpts);
        if (!isLatest()) return;
        setQuarterly(r.data.data);
      } else if (reportType === 'fy') {
        const r = await axios.get(`${API}/sales-analytics/fy-comparison?fy_start_years=${fySelectionCsv}${scopeQS}`, reqOpts);
        if (!isLatest()) return;
        setFyComp(r.data.data);
      } else if (reportType === 'trend') {
        const r = await axios.get(`${API}/sales-analytics/trend?metric=${trendMetric}&granularity=${trendGran}&fy_start_year=${fyStartYear}${scopeQS}`, reqOpts);
        if (!isLatest()) return;
        setTrend(r.data.data);
      } else if (reportType === 'channel') {
        const [r, rMonthly] = await Promise.all([
          axios.get(`${API}/sales-analytics/channel-mix?${kpiQS}${scopeQS}`, reqOpts),
          axios.get(`${API}/sales-analytics/channel-mix/monthly?fy_start_year=${fyStartYear}${scopeQS}`, reqOpts),
        ]);
        if (!isLatest()) return;
        setMix(r.data.data);
        setMixMonthly(rMonthly.data.data);
      }
    } catch (e) {
      // Aborted-by-us / CanceledError must be silent — that's the
      // race-prevention path, not a real failure.
      if (axios.isCancel?.(e) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError'
          || e?.name === 'AbortError') {
        return;
      }
      console.error('Sales analytics fetch failed', e);
      toast.error('Failed to load report');
    } finally {
      // Only the LATEST request clears the busy flag (older losers
      // shouldn't yank the spinner off mid-load).
      if (mySeq === reqSeqRef.current) setBusy(false);
    }
  }, [API, getHeaders, reportType, kpiQS, scopeQS, rangeStart, rangeEnd,
       fyStartYear, fySelectionCsv, trendMetric, trendGran]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // If the active portfolio no longer supports the chosen trend metric
  // (e.g. switched from a hotel to a dine-in-only portfolio while
  // 'room_revenue' was selected), auto-reset to the safe default.
  useEffect(() => {
    const applicable = kpis?.applicable_metrics;
    if (Array.isArray(applicable) && applicable.length > 0
        && !applicable.includes(trendMetric)) {
      setTrendMetric('total_revenue');
    }
  }, [kpis, trendMetric]);

  // Build the metadata block the backend stamps onto the export header.
  const buildMetadata = useCallback(() => {
    const meta = {
      company:      user?.tenant_name || user?.company_name || 'RestroPulse',
      portfolios:   portfolioName,
      outlets:      outletNames,
      report_type:  ({
        daily: 'Daily Analysis', monthly: 'Monthly Analysis',
        quarterly: 'Quarterly Analysis', fy: 'FY Analysis',
        trend: 'Trend Analysis', channel: 'Channel Mix',
      })[reportType] || '—',
      generated_on: format(new Date(), 'dd MMM yyyy, HH:mm'),
      generated_by: user?.name || user?.email || '—',
    };
    if (reportType === 'daily') {
      meta.date_range = `${format(rangeStart, 'dd MMM yyyy')} – ${format(rangeEnd, 'dd MMM yyyy')}`;
    } else if (reportType === 'fy') {
      meta.fy = fySelectionCsv.split(',').map(y => `FY ${y}-${String(Number(y) + 1).slice(-2)}`).join(', ');
    } else {
      meta.fy = `FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`;
    }
    return meta;
  }, [user, portfolioName, outletNames, reportType, rangeStart, rangeEnd,
       fyStartYear, fySelectionCsv]);

  const downloadExport = async (kind) => {
    try {
      const payload = {
        metadata:      buildMetadata(),
        daily:         reportType === 'daily'     ? daily     : null,
        monthly:       reportType === 'monthly'   ? monthly   : null,
        quarterly:     reportType === 'quarterly' ? quarterly : null,
        fy_comparison: reportType === 'fy'        ? fyComp    : null,
        channel_mix:   reportType === 'channel'   ? mix       : null,
      };
      const resp = await axios.post(
        `${API}/sales-analytics/export/${kind}`,
        payload,
        { headers: getHeaders(), responseType: 'blob' });
      const blob = new Blob([resp.data], {
        type: kind === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_${reportType}_${format(new Date(), 'yyyyMMdd_HHmm')}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`${kind.toUpperCase()} export failed`);
    }
  };

  // ── Render ────────────────────────────────────────────────────
  const reportOptions = [
    { id: 'daily',     label: 'Daily' },
    { id: 'monthly',   label: 'Monthly' },
    { id: 'quarterly', label: 'Quarterly' },
    { id: 'fy',        label: 'FY' },
    { id: 'trend',     label: 'Trend' },
    { id: 'channel',   label: 'Channel Mix' },
  ];

  const fyYears = [currentFY - 3, currentFY - 2, currentFY - 1, currentFY, currentFY + 1];

  return (
    <div className="space-y-4" data-testid="sales-analytics-view">
      {/* Report-type selector + dynamic filter row */}
      <Card className="p-3 border border-slate-200">
        <div className="flex flex-wrap items-center gap-2">
          {/* Report-type pill group */}
          <div className="inline-flex rounded-md bg-slate-100 p-0.5" data-testid="sa-report-type">
            {reportOptions.map(r => (
              <button key={r.id} onClick={() => setReportType(r.id)}
                className={`h-8 px-3 text-xs rounded transition ${
                  reportType === r.id
                    ? 'bg-[#0E5236] text-white shadow-sm'
                    : 'text-slate-600 hover:text-[#0E5236]'}`}
                data-testid={`sa-report-${r.id}`}>{r.label}</button>
            ))}
          </div>

          <span className="text-slate-300 mx-1">|</span>

          {isManagerView ? (
            <div className="inline-flex items-center gap-1.5" data-testid="sa-manager-outlet-context">
              {(user?.assigned_outlets || []).map((o) => (
                <span key={o.id}
                      data-testid={`sa-manager-outlet-chip-${o.id}`}
                      className="inline-flex items-center h-8 px-3 rounded-md text-xs font-semibold bg-[color:var(--color-primary-tint)] text-[color:var(--color-primary-hover)] border border-[color:var(--color-primary)]/20">
                  {o.name}
                </span>
              ))}
            </div>
          ) : (
          <>
          {/* Shared: portfolio + outlet — staged via "pending" state to
              eliminate the per-checkbox fetch storm (ITER240 FIX). */}
          <OutletFilter
            portfolioId={pendingPortfolioId}
            outletIds={pendingOutletIds}
            onChange={({ portfolioId: pid, outletIds: oids }) => {
              if (!hydratedRef.current) {
                hydratedRef.current = true;
                setPortfolioId(pid || '');
                setOutletIds(oids || []);
              }
              setPendingPortfolioId(pid || '');
              setPendingOutletIds(oids || []);
            }}
          />
          <Button
            size="sm"
            disabled={!scopeDirty}
            onClick={applyScope}
            data-testid="sa-apply-scope"
            className={`h-9 ${scopeDirty
              ? 'bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
            Apply
          </Button>
          {scopeDirty && (
            <button type="button"
              onClick={resetScope}
              data-testid="sa-reset-scope"
              className="text-[11px] text-slate-500 hover:text-slate-700 underline underline-offset-2">
              reset
            </button>
          )}
          </>
          )}

          {/* Report-specific time controls — STRICTLY one set at a time */}
          {reportType === 'daily' && (
            <>
              <Popover open={rangeStartOpen} onOpenChange={setRangeStartOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9" data-testid="sa-range-start">
                    From {format(rangeStart, 'dd MMM yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={rangeStart}
                    onSelect={(d) => { if (d) { setRangeStart(d); setRangeStartOpen(false); } }}/>
                </PopoverContent>
              </Popover>
              <Popover open={rangeEndOpen} onOpenChange={setRangeEndOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9" data-testid="sa-range-end">
                    To {format(rangeEnd, 'dd MMM yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={rangeEnd}
                    onSelect={(d) => { if (d) { setRangeEnd(d); setRangeEndOpen(false); } }}/>
                </PopoverContent>
              </Popover>
            </>
          )}

          {(reportType === 'monthly' || reportType === 'quarterly' ||
            reportType === 'trend'   || reportType === 'channel') && (
            <Select value={String(fyStartYear)} onValueChange={(v) => setFyStartYear(Number(v))}>
              <SelectTrigger className="w-[140px] h-9" data-testid="sa-fy-select">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                {fyYears.map(y => (
                  <SelectItem key={y} value={String(y)}>FY {y}-{String(y + 1).slice(-2)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {reportType === 'fy' && (
            <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-md border border-slate-200" data-testid="sa-fy-multi">
              {fyYears.map(y => {
                const selected = fySelectionCsv.split(',').map(s => s.trim()).includes(String(y));
                return (
                  <button key={y}
                    onClick={() => {
                      const cur = fySelectionCsv.split(',').map(s => s.trim()).filter(Boolean);
                      const next = selected ? cur.filter(s => s !== String(y))
                                            : [...cur, String(y)].sort();
                      setFySelectionCsv(next.join(','));
                    }}
                    className={`h-7 px-2 text-[11px] rounded ${
                      selected ? 'bg-[#0E5236] text-white' : 'text-slate-600 hover:bg-slate-200'}`}>
                    FY {y}-{String(y + 1).slice(-2)}
                  </button>
                );
              })}
            </div>
          )}

          {reportType === 'trend' && (
            <>
              <Select value={trendMetric} onValueChange={setTrendMetric}>
                <SelectTrigger className="w-[170px] h-9" data-testid="sa-trend-metric">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  {(kpis?.applicable_metrics || [
                    'total_revenue', 'net_sales', 'food_sale', 'beverage_sale',
                    'orders', 'covers', 'total_apc', 'food_cost_pct',
                    'beverage_cost_pct']).map(m => (
                    <SelectItem key={m} value={m}>
                      {(kpis?.metric_labels?.[m]) || METRIC_LABEL[m] || m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={trendGran} onValueChange={setTrendGran}>
                <SelectTrigger className="w-[140px] h-9" data-testid="sa-trend-gran">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="ytd">YTD (cumulative)</SelectItem>
                  <SelectItem value="fy">Financial Year</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}

          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadExport('excel')}
                    className="h-9" data-testid="sa-export-excel">
              <Download className="w-4 h-4 mr-1"/> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadExport('pdf')}
                    className="h-9" data-testid="sa-export-pdf">
              <FileText className="w-4 h-4 mr-1"/> PDF
            </Button>
          </div>
        </div>
        {busy && <div className="text-xs text-slate-500 mt-2">Loading…</div>}
      </Card>

      {/* KPI Cards — driven entirely by backend capability engine. */}
      {kpis && <KpiCards totals={kpis.totals}
                          groups={kpis.applicable_groups}
                          labels={kpis.metric_labels}/>}

      {/* Report body */}
      {reportType === 'daily' && daily && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-700">
            Daily Sales Analysis · {format(rangeStart, 'dd MMM')} – {format(rangeEnd, 'dd MMM yyyy')}
          </div>
          <MatrixTable labels={daily.dates} matrix={daily.matrix}
                        metrics={daily.metrics} summary={daily.summary}
                        metricLabels={daily.metric_labels}
                        summaryLabel="Period Total"/>
          <div className="text-[11px] text-slate-500">
            ↗ better vs same weekday last week · ↘ worse · → ±3% band
          </div>
        </div>
      )}

      {reportType === 'monthly' && monthly && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-700">
            Monthly Sales Analysis · FY {monthly.fy}
          </div>
          <MatrixTable labels={monthly.months} matrix={monthly.matrix}
                        metrics={monthly.metrics} summary={monthly.summary}
                        metricLabels={monthly.metric_labels}
                        summaryLabel="FY Total"/>
          <div className="text-[11px] text-slate-500">
            ↗ better vs previous month · ↘ worse · → ±3% band
          </div>
        </div>
      )}

      {reportType === 'quarterly' && quarterly && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-700">
            Quarterly Sales Analysis · FY {quarterly.fy}
          </div>
          <MatrixTable labels={quarterly.quarters} matrix={quarterly.matrix}
                        metrics={quarterly.metrics} summary={quarterly.summary}
                        metricLabels={quarterly.metric_labels}
                        summaryLabel="FY Total"/>
          <div className="text-[11px] text-slate-500">
            ↗ better vs previous quarter · ↘ worse · → ±3% band
          </div>
        </div>
      )}

      {reportType === 'fy' && fyComp && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-700">Multi-FY Comparison</div>
          <MatrixTable labels={fyComp.fys} matrix={fyComp.matrix}
                        metrics={fyComp.metrics} summary={fyComp.summary}
                        metricLabels={fyComp.metric_labels}
                        summaryLabel="Latest FY"/>
          <div className="text-[11px] text-slate-500">
            ↗ better vs prior FY · ↘ worse · → ±3% band
          </div>
        </div>
      )}

      {reportType === 'trend' && trend && (
        <Card className="p-3 border border-slate-200">
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={trend.series.map(s => ({ ...s, label: fmtMonth(s.label) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8DF"/>
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#5B6A78' }}/>
              <YAxis tick={{ fontSize: 11, fill: '#5B6A78' }}
                     tickFormatter={(v) => fmtMetric(trendMetric, v)}/>
              <Tooltip formatter={(v) => fmtMetric(trendMetric, v)}/>
              <Legend/>
              <Line type="monotone" dataKey="value" stroke="#0E5236" strokeWidth={2.4}
                    dot={{ r: 3 }} name={METRIC_LABEL[trendMetric] || trendMetric}/>
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {reportType === 'channel' && <ChannelMixCard data={mix}/>}
      {reportType === 'channel' && <ChannelMixMonthlyCard data={mixMonthly} fyStartYear={fyStartYear}/>}
    </div>
  );
}
